"use client";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send,
  Paperclip,
  Plus,
  MessageSquare,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  UserSquare,
  Headset,
  Workflow as WorkflowIcon,
  RotateCw,
  ExternalLink,
  Search,
} from "lucide-react";
import { Textarea } from "../../components/ui/textarea";
import { useZeno } from "../../hooks/useZeno";
import zenoAvatar from "../../assets/zeno-workflow.png";
import zenoChatHero from "../../assets/zeno-chat.webp";
import AuthContext from "../../AuthContext";
import MobileMenuButton from "../../components/Sidebar/MobileMenuButton";
import { useHeaderSlot } from "../../components/Sidebar/MobileNavContext";
import { useOrganizations, useProjects } from "../../hooks/useOrganizations";
import QuickCreateAvatar from "../../components/Chat/QuickCreateAvatar";
import QuickCreateAgent from "../../components/Chat/QuickCreateAgent";
import ZenoResourceCard from "../../components/Chat/ZenoResourceCard";
import ZenoToolTrace from "../../components/Chat/ZenoToolTrace";
import ZenoOptions from "../../components/Chat/ZenoOptions";
import ZenoCapabilities from "../../components/Chat/ZenoCapabilities";
import { parseZenoCards } from "../../components/Chat/zenoCards";
import { useAssistantContext } from "../../contexts/AssistantContext";
import WorkflowService from "../../services/workflow.service";
import TemplateService from "../../services/template.service";
import ZenitThinking from "../../components/ui/ZenitThinking";
import { estimateBudgetForAttachments } from "../../utils/tokenEstimation";
import AssistantService from "../../services/assistant.service";
import { isLongText, LongTextCard } from "../../components/Chat/chatHelpers";

const MAX_CHAT_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ZENO_ASSISTANT_ID = '00000000-0000-0000-0000-0000000000e0';

// Shared markdown renderers for Zeno's replies (purple-accented, links open
// in a new tab). `node` is stripped so it never leaks onto DOM elements.
const MD_COMPONENTS = {
  h1: ({ node, ...props }) => <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#c7aaff' }} {...props} />,
  h2: ({ node, ...props }) => <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 10, color: '#b48be6' }} {...props} />,
  h3: ({ node, ...props }) => <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: '#a47fd1' }} {...props} />,
  ul: ({ node, ...props }) => <ul style={{ marginLeft: 24, marginBottom: 12, listStyleType: 'disc' }} {...props} />,
  ol: ({ node, ...props }) => <ol style={{ marginLeft: 24, marginBottom: 12, listStyleType: 'decimal' }} {...props} />,
  li: ({ node, ...props }) => <li style={{ marginBottom: 4 }} {...props} />,
  strong: ({ node, ...props }) => <strong style={{ fontWeight: 700 }} {...props} />,
  p: ({ node, ...props }) => <p style={{ marginBottom: 12, lineHeight: 1.6 }} {...props} />,
  a: ({ node, ...props }) => <a style={{ color: '#b98bff', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />,
  // Fenced code blocks land in <pre>; wrap + constrain so long lines break
  // inside the bubble instead of overflowing and getting clipped.
  pre: ({ node, ...props }) => (
    <pre
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 12,
        maxWidth: '100%',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: '0.85em',
      }}
      {...props}
    />
  ),
  code: ({ node, className, children, ...props }) => {
    // Block code (inside <pre>) has a language class or spans multiple lines —
    // render it plain so the <pre> owns the styling. Inline code keeps the pill.
    const isBlock = (className && className.includes('language-')) || String(children).includes('\n');
    if (isBlock) {
      return (
        <code className={className} style={{ background: 'transparent', padding: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: 6, fontSize: '0.85em', wordBreak: 'break-word' }} {...props}>
        {children}
      </code>
    );
  },
};

// Starter prompts shown in the empty state and the Zeno side panel.
const STARTER_PROMPTS = [
  "WhatsApp support agent",
  "Lead-capture workflow",
  "Order updates in Shopify",
  "Daily report bot",
];

// Conversations sidebar resize bounds (px).
// El ancho del rail ya no es una constante: el Sidebar se pliega y despliega, y
// lo publica como --rail-w. Este valor solo es el respaldo para el primer
// render, antes de que el Sidebar monte.
const CONV_RAIL_FALLBACK = 80;

// Lee el ancho real del rail para las cuentas en JS. Lo que se puede resolver en
// CSS usa calc(var(--rail-w) + ...) directo y no pasa por aquí.
const railWidthPx = () => {
  if (typeof window === 'undefined') return CONV_RAIL_FALLBACK;
  const root = document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue('--rail-w').trim();
  if (!raw) return CONV_RAIL_FALLBACK;
  if (raw.endsWith('rem')) return parseFloat(raw) * parseFloat(getComputedStyle(root).fontSize);
  return parseFloat(raw) || CONV_RAIL_FALLBACK;
};
// Gap (px) between the app rail and the conversations card, and between that
// card and the chat panel — so the card floats like the other rounded panels.
const CONV_GAP = 16;
const CONV_MIN = 200;
const CONV_MAX = 380;
const CONV_DEFAULT = 248;
const CONV_STORAGE_KEY = 'zenoConvWidth2'; // bumped to reset any old wide value

export default function HomeView() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showZenoPanel, setShowZenoPanel] = useState(true);
  const [showQuickAvatar, setShowQuickAvatar] = useState(false);
  const [showQuickAgent, setShowQuickAgent] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  // Mobile layout: chat is full-width; the conversations rail and Zeno panel
  // become overlay drawers (ChatGPT-style). Breakpoint matches Tailwind lg.
  const [isMobile, setIsMobile] = useState(false);
  const [showConvDrawer, setShowConvDrawer] = useState(false);
  // La cabecera del chat es la cabecera de la pantalla, así que le toca a ella
  // pintar la hamburguesa de la app. Sin esto el shell dibujaba la suya
  // flotando en la esquina y caía justo encima del botón del historial, que
  // vive en ese mismo sitio: se tocaba el menú de la app creyendo abrir los
  // chats, y el historial parecía no existir.
  const isPrimaryHeader = useHeaderSlot();
  const { user } = useContext(AuthContext);
  const { currentAccount } = useOrganizations();
  const { projects } = useProjects();
  // Recently created resources for the right panel (FE words: agents = BE
  // assistants, avatars = BE assistant_templates).
  const { assistants, loadAssistants } = useAssistantContext();
  const recentBy = (list) =>
    [...(Array.isArray(list) ? list : [])]
      .sort(
        (a, b) =>
          new Date(b.created_at || b.createdAt || b.updated_at || b.updatedAt || 0) -
          new Date(a.created_at || a.createdAt || a.updated_at || a.updatedAt || 0)
      )
      .slice(0, 3);
  // Agents = account's assistants (already account-scoped in the context).
  const recentAgents = recentBy(assistants);
  const firstName = (user?.name || user?.given_name || user?.first_name || '')
    .toString()
    .trim()
    .split(' ')[0];
  const greeting = `Hey${firstName ? `, ${firstName}` : ''}. Ready to build?`;
  // Avatars = the account's templates (public + this account's private ones),
  // fetched account-scoped and newest-first. The context's `templates` uses a
  // deprecated unscoped endpoint, so we fetch our own here.
  const [recentAvatars, setRecentAvatars] = useState([]);
  // Workflows are project-scoped (no account-wide list), so aggregate the newest
  // across the user's projects. Bounded + resilient to keep the panel light.
  const [recentWorkflows, setRecentWorkflows] = useState([]);
  const [refreshingRecent, setRefreshingRecent] = useState(false);
  const [recentTab, setRecentTab] = useState("avatars"); // avatars | agents | workflows

  const fetchRecentAvatars = useCallback(async () => {
    const accountId = currentAccount?.id;
    if (!accountId) {
      setRecentAvatars([]);
      return;
    }
    const res = await TemplateService.listAccountTemplates(accountId, {
      limit: 3,
      sort_by: "updated_at",
      descending: true,
      view: "summary",
    }).catch(() => null);
    const arr = Array.isArray(res?.data) ? res.data : res?.data?.templates || [];
    setRecentAvatars(arr.slice(0, 3));
  }, [currentAccount?.id]);

  const fetchRecentWorkflows = useCallback(async () => {
    const accountId = currentAccount?.id;
    const projList = Array.isArray(projects) ? projects : [];
    if (!accountId || projList.length === 0) {
      setRecentWorkflows([]);
      return;
    }
    const perProject = await Promise.all(
      projList.slice(0, 8).map((p) =>
        WorkflowService.listWorkflows({
          account_id: accountId,
          project_id: p.id,
          limit: 5,
          sort_by: "updated_at",
          descending: true,
        })
          .then((r) => (r?.success ? r.data?.workflows || [] : []))
          .catch(() => [])
      )
    );
    const merged = perProject
      .flat()
      .sort(
        (a, b) =>
          new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0) -
          new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0)
      )
      .slice(0, 3);
    setRecentWorkflows(merged);
  }, [currentAccount?.id, projects]);

  // Refresh all three recent lists (avatars, agents, workflows) at once.
  const handleRefreshRecent = useCallback(async () => {
    if (refreshingRecent) return;
    setRefreshingRecent(true);
    try {
      await Promise.all([
        fetchRecentAvatars(),
        loadAssistants?.(),
        fetchRecentWorkflows(),
      ]);
    } finally {
      setRefreshingRecent(false);
    }
  }, [refreshingRecent, fetchRecentAvatars, loadAssistants, fetchRecentWorkflows]);
  const [convWidth, setConvWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem(CONV_STORAGE_KEY));
    return saved >= CONV_MIN && saved <= CONV_MAX ? saved : CONV_DEFAULT;
  });
  const convResizingRef = useRef(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const avatarBtnRef = useRef(null); // "New avatar" button — anchors the Quick create popover
  const [avatarPopover, setAvatarPopover] = useState(null); // { top, right, maxHeight } | null
  const agentBtnRef = useRef(null); // "New agent" button — anchors its popover
  const [agentPopover, setAgentPopover] = useState(null); // { top, right, maxHeight, tailTop } | null
  const moreActionsBtnRef = useRef(null); // "More actions" button — anchors its popover
  const [moreActionsPopover, setMoreActionsPopover] = useState(null); // { top, right, tailTop } | null
  const lastSentRef = useRef(null); // Save last sent attachments/message for budget error recovery
  const [attachments, setAttachments] = useState([]);
  const [zenoContextWindow, setZenoContextWindow] = useState(null);
  const [zenoRootPrompt, setZenoRootPrompt] = useState('');

    const { messages, loading, isTyping, sendMessageStream, chatHistories, loadingChats,
      loadZenoChats, loadConversation, startNewConversation, currentChatHistoryId,
      contentBudget, setContentBudget, zenoOnline, checkZenoHealth,
      chatsHasMore, loadingMoreChats, loadMoreChats } = useZeno();

    const [chatError, setChatError] = useState("");
    // Recent-chats search (title) + infinite-scroll sentinel.
    const [showChatSearch, setShowChatSearch] = useState(false);
    const [chatSearch, setChatSearch] = useState("");
    const chatsSentinelRef = useRef(null);

  // Asegurar valores seguros
  const safeChatHistories = Array.isArray(chatHistories) ? chatHistories : [];
  const safeLoadingChats = loadingChats || false;

  const phrases = [
    "Ask Zeno to create a prototype...",
    "Build an AI agent...",
    "Design a workflow automation...",
    "Create a Shopify integration...",
    "Generate a custom dashboard...",
    "Develop a chatbot for your business...",
  ];

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    const typingSpeed = isDeleting ? 30 : 80;
    const pauseTime = isDeleting ? 500 : 2000;

    if (!isDeleting && charIndex === currentPhrase.length) {
      setTimeout(() => setIsDeleting(true), pauseTime);
      return;
    }

    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const timeout = setTimeout(() => {
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
      setPlaceholder(
        currentPhrase.substring(0, charIndex + (isDeleting ? -1 : 1))
      );
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phraseIndex]);

  // Cargar lista de chats al montar el componente
  useEffect(() => {
    loadZenoChats();
  }, [loadZenoChats]);

  // Debounced title search — reload the recent-chats list when the query
  // changes. Skips the first run (mount is handled by the effect above).
  const chatSearchFirstRun = useRef(true);
  useEffect(() => {
    if (chatSearchFirstRun.current) { chatSearchFirstRun.current = false; return; }
    const t = setTimeout(() => { loadZenoChats({ search: chatSearch }); }, 300);
    return () => clearTimeout(t);
  }, [chatSearch, loadZenoChats]);

  // Infinite scroll for the recent-chats list: load the next page when the
  // sentinel near the bottom scrolls into view.
  useEffect(() => {
    const el = chatsSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && chatsHasMore && !loadingMoreChats && !loadingChats) {
          loadMoreChats();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [chatsHasMore, loadingMoreChats, loadingChats, loadMoreChats, safeChatHistories.length]);

  // Health check for the "Online" indicator. No periodic polling — just check
  // once on page load and again when the user returns to the tab. Reachability
  // rarely changes, so that's fresh enough without hitting the endpoint on a timer.
  useEffect(() => {
    checkZenoHealth();
    const onVisible = () => { if (!document.hidden) checkZenoHealth(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [checkZenoHealth]);

  // Fetch Zeno's context_window once on mount for client-side budget estimation
  useEffect(() => {
    AssistantService.getAssistant(ZENO_ASSISTANT_ID).then(result => {
      if (result.success && result.data?.llmContextWindow) {
        setZenoContextWindow(result.data.llmContextWindow);
      }
      if (result.success && result.data?.config?.rootPrompt) {
        setZenoRootPrompt(result.data.config.rootPrompt);
      }
    }).catch(() => {});
  }, []);

  // Scroll automático al final cuando hay nuevos mensajes
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [message]);

  useEffect(() => {
    window.localStorage.setItem(CONV_STORAGE_KEY, String(convWidth));
  }, [convWidth]);

  // Track mobile vs desktop. On mobile the Zeno panel starts collapsed (drawer);
  // on desktop it's open inline.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const apply = () => {
      setIsMobile(mq.matches);
      setShowZenoPanel(!mq.matches);
      if (!mq.matches) setShowConvDrawer(false);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Anchor the Quick create → Avatar popover to the "New avatar" button,
  // opening leftward over the chat (never covering the right menu).
  useEffect(() => {
    if (!showQuickAvatar) {
      setAvatarPopover(null);
      return;
    }
    const compute = () => {
      const el = avatarBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const GAP = 14; // space between the popover tail and the button
      const MARGIN = 16;
      const top = Math.max(MARGIN, r.top - 8);
      setAvatarPopover({
        top,
        right: Math.round(window.innerWidth - r.left + GAP),
        maxHeight: window.innerHeight - top - MARGIN,
        tailTop: Math.max(12, r.top + r.height / 2 - top - 7), // point the tail at the button
      });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [showQuickAvatar]);

  // Anchor the "New agent" popover to its button (same mechanism as the avatar one).
  useEffect(() => {
    if (!showQuickAgent) {
      setAgentPopover(null);
      return;
    }
    const compute = () => {
      const el = agentBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const GAP = 14;
      const MARGIN = 16;
      const top = Math.max(MARGIN, r.top - 8);
      setAgentPopover({
        top,
        right: Math.round(window.innerWidth - r.left + GAP),
        maxHeight: window.innerHeight - top - MARGIN,
        tailTop: Math.max(12, r.top + r.height / 2 - top - 7),
      });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [showQuickAgent]);

  // Anchor the "More actions" popover to its button, opening leftward over the chat.
  useEffect(() => {
    if (!showMoreActions) {
      setMoreActionsPopover(null);
      return;
    }
    const compute = () => {
      const el = moreActionsBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const GAP = 12;
      const MARGIN = 16;
      const top = Math.max(MARGIN, r.top - 8);
      setMoreActionsPopover({
        top,
        right: Math.round(window.innerWidth - r.left + GAP),
        tailTop: Math.max(12, r.top + r.height / 2 - top - 7),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [showMoreActions]);

  // Load recent avatars + workflows on mount / when account or projects change.
  useEffect(() => {
    fetchRecentAvatars();
  }, [fetchRecentAvatars]);
  useEffect(() => {
    fetchRecentWorkflows();
  }, [fetchRecentWorkflows]);

  // Drag-to-resize the conversations sidebar. Pointer capture keeps the drag
  // alive even when the cursor leaves the thin handle, so it never "sticks"
  // after the first move.
  const startConvResize = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    convResizingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };
  const moveConvResize = (e) => {
    if (!convResizingRef.current) return;
    const w = Math.min(CONV_MAX, Math.max(CONV_MIN, e.clientX - railWidthPx() - CONV_GAP));
    setConvWidth(w);
  };
  const endConvResize = (e) => {
    if (!convResizingRef.current) return;
    convResizingRef.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  // Restore attachments and message when content budget error is detected (from backend)
  useEffect(() => {
    if (contentBudget?.rejected_blocks?.length > 0 && lastSentRef.current) {
      const { message: savedMsg, attachments: savedAttachments } = lastSentRef.current;
      if (savedMsg) setMessage(savedMsg);
      if (Array.isArray(savedAttachments) && savedAttachments.length > 0) {
        const rejectedNames = new Set((contentBudget.rejected_blocks || []).map(b => b.filename).filter(Boolean));
        setAttachments(savedAttachments.map(a => ({ ...a, budgetRejected: rejectedNames.size === 0 || rejectedNames.has(a.name) })));
      }
      lastSentRef.current = null;
    }
  }, [contentBudget]);

  // Estimate content budget when attachments change
  useEffect(() => {
    if (!attachments.length) {
      setContentBudget(null);
      return;
    }
    const budget = estimateBudgetForAttachments(attachments, zenoContextWindow, messages, zenoRootPrompt);
    if (budget) {
      const rejectedNames = new Set((budget.rejected_blocks || []).map(b => b.filename).filter(Boolean));
      setAttachments(prev => prev.map(a => ({ ...a, budgetRejected: rejectedNames.has(a.name) })));
      setContentBudget(budget);
    } else {
      setAttachments(prev => prev.map(a => ({ ...a, budgetRejected: false })));
      setContentBudget(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.map(a => `${a.name}:${a.size}`).join('|'), zenoContextWindow, zenoRootPrompt, messages.length]);

  const formatAttachmentSize = (size) => {
    if (!Number.isFinite(size) || size <= 0) return '0 B';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

  const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsArrayBuffer(file);
  });

  const clearAttachments = () => {
    setAttachments([]);
    setContentBudget(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const removeAttachmentAt = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const buildUserContentBlocks = (messageText, currentAttachments) => {
    const contentBlocks = [];
    const trimmedMessage = messageText.trim();

    if (trimmedMessage) {
      contentBlocks.push({
        type: 'text',
        data: { text: trimmedMessage },
        order: contentBlocks.length + 1,
      });
    }

    (currentAttachments || []).forEach((att) => {
      if (att?.contentBlock) {
        contentBlocks.push({
          ...att.contentBlock,
          order: contentBlocks.length + 1,
        });
      }
    });

    return contentBlocks;
  };

  const prepareAttachmentFromFile = async (file) => {
    let contentBlock;
    let previewUrl = null;

    if (file.type.startsWith('image/')) {
      const dataUrl = await readFileAsDataUrl(file);
      previewUrl = dataUrl;
      contentBlock = {
        type: 'image',
        data: {
          url: dataUrl,
          mime_type: file.type,
          filename: file.name,
        },
        order: 1,
      };
    } else {
      const arrayBuffer = await readFileAsArrayBuffer(file);
      contentBlock = {
        type: 'file',
        data: {
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          content: arrayBufferToBase64(arrayBuffer),
        },
        order: 1,
      };
    }

    return {
      name: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      previewUrl,
      contentBlock,
    };
  };

  const handleAttachmentSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const oversized = files.filter(f => f.size > MAX_CHAT_ATTACHMENT_SIZE_BYTES);
    const valid = files.filter(f => f.size <= MAX_CHAT_ATTACHMENT_SIZE_BYTES);

    if (oversized.length > 0) {
      setChatError(`Some files exceed the 10 MB limit and were skipped: ${oversized.map(f => f.name).join(', ')}`);
    }

    if (valid.length === 0) {
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      return;
    }

    try {
      const prepared = await Promise.all(valid.map(prepareAttachmentFromFile));
      if (oversized.length === 0) setChatError('');
      setAttachments(prev => [...prev, ...prepared]);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    } catch (attachmentError) {
      console.error('Error preparing attachment for Zeno:', attachmentError);
      setChatError('One or more files could not be prepared.');
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const renderAttachmentPreview = () => {
    if (!attachments.length) return null;

    return (
      <div className="mb-3 flex flex-col gap-2">
        {attachments.map((att, idx) => {
          const isRejected = att.budgetRejected;
          return (
            <div
              key={`${att.name}-${idx}`}
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${isRejected ? 'border-red-500/50 bg-red-500/10' : 'border-white/10 bg-white/5'}`}
            >
              {att.previewUrl ? (
                <img
                  src={att.previewUrl}
                  alt={att.name}
                  className="h-12 w-12 rounded-xl object-cover border border-white/10"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-xs font-semibold text-gray-200">
                  FILE
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{att.name}</p>
                <p className="text-xs text-gray-400">
                  {isRejected && <span className="font-semibold text-red-400">Exceeds token limit · </span>}
                  {att.mimeType} · {formatAttachmentSize(att.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeAttachmentAt(idx)}
                className="rounded-full p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Remove attachment"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMessageContentBlocks = (messageItem) => {
    if (!Array.isArray(messageItem.contentBlocks) || messageItem.contentBlocks.length === 0) {
      return null;
    }

    const rendered = messageItem.contentBlocks.map((block, index) => {
      // Los bloques `options` se renderizan como botones aparte (ZenoOptions),
      // no como contenido del bubble. Sin este skip caerían al JSON.stringify
      // de abajo y se verían como JSON crudo.
      if (block.type === 'options') {
        return null;
      }

      if (block.type === 'image' && block.data?.url) {
        return (
          <div key={`${messageItem.id || 'message'}-image-${index}`} className="space-y-3">
            <img
              src={block.data.url}
              alt={block.data.filename || 'Attached image'}
              className="rounded-2xl max-w-full h-auto border border-white/10"
            />
            {block.data.filename && (
              <p className="text-xs text-gray-300">{block.data.filename}</p>
            )}
          </div>
        );
      }

      if (block.type === 'file') {
        return (
          <div
            key={`${messageItem.id || 'message'}-file-${index}`}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
          >
            <p className="text-sm font-medium text-white truncate">{block.data?.filename || 'Attached file'}</p>
            <p className="text-xs text-gray-400 mt-1">{block.data?.mime_type || 'application/octet-stream'}</p>
          </div>
        );
      }

      let markdown = '';
      if (block.data && typeof block.data === 'object') {
        if (typeof block.data.text === 'string') markdown = block.data.text;
        else if (Array.isArray(block.data)) markdown = block.data.map((value) => typeof value === 'string' ? value : JSON.stringify(value)).join('\n');
        else markdown = JSON.stringify(block.data, null, 2);
      } else if (typeof block.data === 'string') {
        markdown = block.data;
      }

      if (!markdown && block.type) markdown = block.type;

      return markdown && markdown.trim() ? (
        <ReactMarkdown
          key={`${messageItem.id || 'message'}-text-${index}`}
          remarkPlugins={[remarkGfm]}
          components={MD_COMPONENTS}
        >
          {markdown}
        </ReactMarkdown>
      ) : null;
    }).filter(Boolean);

    // Si todos los bloques se filtraron (p.ej. un mensaje de solo `options`),
    // no devolvemos un array vacío para no pintar un bubble vacío.
    return rendered.length > 0 ? rendered : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!message.trim() && attachments.length === 0) || loading) return;
    if (contentBudget?.rejected_blocks?.length > 0) return;
    const userMessage = message;
    // Save for potential budget error recovery
    lastSentRef.current = { message: userMessage, attachments };
    const contentBlocks = buildUserContentBlocks(userMessage, attachments);
    setMessage("");
    clearAttachments();
    setChatError("");
    setContentBudget(null);
    // Usar streaming para respuestas más rápidas
    await sendMessageStream({ message: userMessage, contentBlocks });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !(contentBudget?.rejected_blocks?.length > 0)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Elegir una opción de un bloque `options` de Zeno equivale a escribir ese
  // texto y enviarlo: la elección del usuario es su siguiente mensaje.
  const handleSelectOption = async (optionText) => {
    if (!optionText || loading) return;
    setChatError("");
    setContentBudget(null);
    await sendMessageStream({ message: optionText });
  };

  const applyPrompt = (prompt) => {
    setMessage(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Manejar selección de chat desde la historia
  const handleSelectChat = async (chatHistoryId) => {
    try {
      setMessage(""); // Limpiar input
      setChatError("");
      const result = await loadConversation(chatHistoryId);
      if (!result?.success) {
        if (result?.status === 404) {
          setChatError("Could not load the conversation. It may have been deleted. Refreshing chat list...");
          await loadZenoChats();
        } else {
          setChatError("Error loading conversation.");
        }
        console.error('Failed to load conversation:', result?.error);
      } else {
        setChatError("");
        setTimeout(() => {
          if (chatScrollRef.current) {
            chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      setChatError("Unexpected error loading conversation.");
      console.error('Error selecting chat:', error);
    }
  };

  // Iniciar nuevo chat
  const handleNewChat = () => {
    startNewConversation();
    setMessage("");
    setChatError("");
  };

  const canSend = (message.trim() || attachments.length > 0) && !isTyping && !(contentBudget?.rejected_blocks?.length > 0);

  // ---- Composer (shared between empty state and active chat) ----
  const renderComposer = () => (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="rounded-[22px] border border-white/10 bg-white/[0.04] px-2 py-1.5 sm:px-2.5 sm:py-2">
        {renderAttachmentPreview()}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => attachmentInputRef.current?.click()}
            title="Attach a file"
            className="flex-shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white sm:p-2"
          >
            <Paperclip className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent border-0 px-0 text-white text-[15px] sm:text-base placeholder:text-gray-500 resize-none min-h-[24px] sm:min-h-[28px] max-h-[180px] focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none py-0"
            rows={1}
          />

          <button
            type="submit"
            disabled={!canSend}
            className="flex-shrink-0 flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-[12px] sm:rounded-[14px] text-white transition-all duration-300 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #6e27e0, #a147e0)' }}
          >
            <Send className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>
      <p className="mt-2 hidden px-1 text-center text-[11px] text-gray-500 sm:block">
        Enter to send · Shift+Enter for a new line
      </p>
    </form>
  );

  return (
    <div className="h-screen bg-black relative overflow-hidden">
      {/* Match the app-wide ambient (same soft indigo glow the dashboard uses)
          so Zeno reads as part of the app instead of a separate flat surface. */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{
          top: "1rem",
          left: "4rem",
          width: "33rem",
          height: "33rem",
          background:
            "radial-gradient(circle at 25% 25%, rgba(75,0,130,0.8) 0%, rgba(75,0,130,0.4) 50%, transparent 90%)",
          filter: "blur(140px)",
        }}
      />

      {/* Mobile: backdrop behind the conversations drawer (leaves the app rail
          exposed at the left so global nav stays one tap away). */}
      {isMobile && showConvDrawer && (
        <div
          className="fixed inset-y-0 right-0 z-[65] bg-black/50 lg:hidden rail-left"
          onClick={() => setShowConvDrawer(false)}
        />
      )}

      {/* Conversations — desktop: persistent left rail; mobile: overlay drawer
          that opens just after the app rail. */}
      <aside
        className={`flex fixed flex-col bg-[#141416] backdrop-blur-xl transition-transform duration-200 overflow-hidden ${
          isMobile
            ? `top-0 bottom-0 z-[70] w-[280px] border-r border-gray-700/30 ${showConvDrawer ? 'translate-x-0' : '-translate-x-[120%]'}`
            : 'top-4 bottom-4 z-50 rounded-[20px] border border-white/10'
        }`}
        style={{
          left: isMobile ? 'var(--rail-w)' : `calc(var(--rail-w) + ${CONV_GAP}px)`,
          ...(isMobile ? {} : { width: `${convWidth}px` }),
        }}
      >
        {/* Drag handle to resize the sidebar (desktop only) */}
        {!isMobile && (
          <div
            onPointerDown={startConvResize}
            onPointerMove={moveConvResize}
            onPointerUp={endConvResize}
            onPointerCancel={endConvResize}
            title="Drag to resize"
            className="absolute top-0 right-0 z-[51] h-full w-2 cursor-ew-resize touch-none transition-colors hover:bg-[#8844F5]/50"
          />
        )}
        <div className="p-4 border-b border-gray-800/80 flex-shrink-0">
          <div className="mb-3 flex items-center justify-between gap-2 px-1">
            <div className="flex min-w-0 items-center gap-2">
              <MessageSquare className="w-4 h-4 flex-shrink-0 text-[#b98bff]" />
              <span className="text-sm font-semibold text-white">Recent chats</span>
            </div>
            <button
              onClick={() => setShowChatSearch((v) => { if (v) setChatSearch(""); return !v; })}
              title="Search chats"
              className={`flex-shrink-0 rounded-lg p-1.5 transition-colors ${showChatSearch ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/10 hover:text-white'}`}
            >
              <Search className="w-4 h-4" />
            </button>
          </div>

          {showChatSearch && (
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
              <input
                autoFocus
                value={chatSearch}
                onChange={(e) => setChatSearch(e.target.value)}
                placeholder="Search by title…"
                className="w-full rounded-lg border border-white/10 bg-white/[0.04] py-1.5 pl-8 pr-7 text-xs text-white placeholder-gray-500 focus:border-[#8844F5]/50 focus:outline-none"
              />
              {chatSearch && (
                <button
                  onClick={() => setChatSearch("")}
                  title="Clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => { handleNewChat(); setShowConvDrawer(false); }}
            className="w-full text-gray-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            New chat
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {safeLoadingChats ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex space-x-3 p-3 bg-gray-800/40 rounded-lg border border-gray-700/40">
                  <div className="rounded-full bg-gray-700 h-8 w-8 flex-shrink-0 skeleton"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-700 rounded w-3/4 skeleton"></div>
                    <div className="h-2 bg-gray-800 rounded w-1/2 skeleton"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : safeChatHistories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
              <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm text-center">{chatSearch ? 'No chats match your search' : 'No conversations yet'}</p>
            </div>
          ) : (
            <>
              {safeChatHistories.map((chat, index) => (
                <button
                  key={chat.id}
                  onClick={() => { handleSelectChat(chat.id); setShowConvDrawer(false); }}
                  className={`group w-full text-left px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    currentChatHistoryId === chat.id
                      ? 'bg-purple-600/25 text-white'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="block w-full truncate text-sm">{chat.title || `Chat ${index + 1}`}</span>
                </button>
              ))}
              {/* Infinite-scroll sentinel: loads the next page when it scrolls in */}
              {chatsHasMore && (
                <div ref={chatsSentinelRef} className="flex justify-center py-3">
                  {loadingMoreChats && <RotateCw className="h-4 w-4 animate-spin text-gray-500" />}
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      <main
        className="relative z-10 h-screen"
        style={{
          paddingLeft: isMobile
            ? 'var(--rail-w)'
            : `calc(var(--rail-w) + ${CONV_GAP + convWidth + CONV_GAP}px)`,
        }}
      >
        <div className="flex h-full gap-4 p-2 lg:py-4 lg:pr-4 lg:pl-0">
          {/* ============ CENTER — Chat ============ */}
          <section className="flex-1 min-w-0 h-full flex flex-col overflow-hidden rounded-[20px] border border-white/10 bg-[#141416]">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-3 lg:px-5">
              <div className="flex min-w-0 items-center gap-2 lg:gap-3">
                {isPrimaryHeader && <MobileMenuButton className="h-10 w-10 rounded-xl" />}
                {/* Antes era otra hamburguesa, idéntica a la del menú de la app
                    y pegada a ella. Dos iconos iguales, uno al lado del otro,
                    abriendo cosas distintas: el del historial lleva ahora el
                    mismo icono con el que el cajón se nombra a sí mismo. */}
                <button
                  onClick={() => setShowConvDrawer(true)}
                  title="Conversations"
                  aria-label="Open conversations"
                  className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
                <img src={zenoAvatar} alt="Zeno" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-sm font-semibold text-white leading-tight">Zeno</h1>
                    {(() => {
                      // Reachability-driven status next to the name.
                      const s = zenoOnline === null
                        ? { label: "Checking…", color: "#9ca3af", glow: false }
                        : zenoOnline
                          ? { label: "Online", color: "#4ade80", glow: true }
                          : { label: "Offline", color: "#f87171", glow: false };
                      return (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-medium"
                          style={{ color: s.color }}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{
                              background: s.color,
                              boxShadow: s.glow ? `0 0 6px ${s.color}` : "none",
                            }}
                          />
                          {s.label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-400 leading-tight">Your workflow copilot</p>
                </div>
              </div>
              {!showZenoPanel && (
                <button
                  onClick={() => setShowZenoPanel(true)}
                  title="Show Zeno panel"
                  className="flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs text-gray-300 transition-colors hover:border-[#8844F5]/50 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Zeno
                </button>
              )}
            </div>

            <input
              ref={attachmentInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={handleAttachmentSelected}
            />

            {messages.length === 0 ? (
              /* Empty conversation — centered greeting + composer (ChatGPT style) */
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col items-center justify-center px-5 py-8">
                  {chatError && (
                    <div className="mb-4 w-full rounded-xl border border-red-700 bg-red-900/70 p-4 text-center text-red-200">
                      {chatError}
                    </div>
                  )}
                  {/* Empty-state hero — the full Zeno scene. Its near-black
                      background is edge-faded into the #141416 panel so the
                      square dissolves (a cutout would wreck the glow). */}
                  <img
                    src={zenoChatHero}
                    alt="Zeno"
                    className="-mb-6 h-48 w-48 object-contain sm:-mb-8 sm:h-60 sm:w-60"
                    style={{
                      WebkitMaskImage:
                        "radial-gradient(circle at 50% 47%, #000 46%, transparent 66%)",
                      maskImage:
                        "radial-gradient(circle at 50% 47%, #000 46%, transparent 66%)",
                    }}
                  />
                  <h2 className="mb-2 text-center text-xl font-bold tracking-tight text-white sm:text-3xl">
                    {greeting}
                  </h2>
                  <p className="mb-6 max-w-md text-center text-sm text-gray-400 sm:text-base">
                    Create AI agents, workflows and integrations just by chatting.
                  </p>
                  <div className="w-full">{renderComposer()}</div>
                  <div className="mt-4 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                    {STARTER_PROMPTS.slice(0, isMobile ? 2 : 4).map((p) => (
                      <button
                        key={p}
                        onClick={() => applyPrompt(p)}
                        className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left text-sm text-gray-200 transition-all hover:border-[#8844F5]/50 hover:bg-white/[0.06]"
                      >
                        <Sparkles className="h-4 w-4 flex-shrink-0 text-[#b98bff]" />
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Scroll area */}
                <div className="flex-1 min-h-0 overflow-y-auto" ref={chatScrollRef}>
                  <div className="mx-auto w-full max-w-3xl px-5 py-6">
                    {chatError && (
                      <div className="mb-4 rounded-xl border border-red-700 bg-red-900/70 p-4 text-center text-red-200">
                        {chatError}
                      </div>
                    )}
                    {contentBudget?.rejected_blocks?.length > 0 && (
                      <div className="mb-4 rounded-xl border border-amber-700 bg-amber-900/60 p-4 text-sm text-amber-200">
                        <p className="mb-1 font-semibold">File exceeds token limit</p>
                        <p className="text-amber-300/80">The attached file is too large for the model&apos;s context window. Please remove it and try with a smaller file.</p>
                      </div>
                    )}
                    <div className="space-y-5">
                    {messages.map((msg, index) => {
                      const hasBlocks =
                        Array.isArray(msg.contentBlocks) && msg.contentBlocks.length > 0;
                      // Derive the text ALWAYS (not skipped when blocks exist): on
                      // reload, card messages carry BOTH contentBlocks and the text
                      // that holds the ```zeno:card``` marker.
                      const rawText =
                        msg.role === 'user'
                          ? (msg.message || msg.content || '')
                          : ([msg.content, msg.response, msg.message].find(
                              (t) => typeof t === 'string' && t.trim()
                            ) || '');
                      const { cleanText, cards } = parseZenoCards(rawText);
                      // Use the stored-blocks renderer (images/attachments) only when
                      // there is no card in the text.
                      const useBlocks = hasBlocks && cards.length === 0;
                      // Bloques `options` (tool ask_user_question). Llegan por dos
                      // rutas: en vivo por metadata.displayBlocks del SSE, y al
                      // recargar dentro de contentBlocks del mensaje persistido.
                      const optionBlocks = [
                        ...(Array.isArray(msg.contentBlocks) ? msg.contentBlocks : []),
                        ...(Array.isArray(msg.metadata?.displayBlocks) ? msg.metadata.displayBlocks : []),
                        ...(Array.isArray(msg.metadata?.display_blocks) ? msg.metadata.display_blocks : []),
                      ].filter((b) => b?.type === 'options' && b?.data);

                      if (msg.role === 'user') {
                        const showBubble = useBlocks || !!cleanText;
                        return (
                          <div key={index} className="flex w-full justify-end">
                            <div className="flex max-w-[85%] flex-col items-end gap-2">
                              {showBubble && (
                                <div
                                  className="w-fit max-w-full rounded-2xl rounded-br-md px-5 py-3 text-white"
                                  style={{ background: 'linear-gradient(135deg, #7b3ff2, #6321c4)' }}
                                >
                                  {useBlocks ? (
                                    renderMessageContentBlocks(msg)
                                  ) : isLongText(cleanText) ? (
                                    <LongTextCard text={cleanText} />
                                  ) : (
                                    <p className="whitespace-pre-wrap leading-relaxed">{cleanText}</p>
                                  )}
                                </div>
                              )}
                              {cards.map((c, ci) => (
                                <ZenoResourceCard key={ci} card={c} />
                              ))}
                            </div>
                          </div>
                        );
                      }

                      const bubble = useBlocks ? (
                        renderMessageContentBlocks(msg)
                      ) : cleanText ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                          {cleanText}
                        </ReactMarkdown>
                      ) : msg.isStreaming ? (
                        <div className="flex items-center gap-2 text-gray-400">
                          <ZenitThinking size={22} />
                          <span>Thinking…</span>
                        </div>
                      ) : (cards.length || optionBlocks.length) ? null : (
                        <span className="text-gray-400 italic">No response</span>
                      );

                      return (
                        <div key={index} className="flex w-full justify-start">
                          <div className="flex w-full max-w-[92%] items-start gap-3">
                            <img src={zenoAvatar} alt="Zeno" className="mt-1 h-8 w-8 flex-shrink-0 rounded-full object-cover" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="text-xs font-medium text-gray-400">Zeno</div>
                              {bubble && (
                                <div className="rounded-2xl border border-white/[0.12] bg-[#212129] px-5 py-4 text-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.25)]">
                                  {bubble}
                                </div>
                              )}
                              {cards.map((c, ci) => (
                                <ZenoResourceCard key={ci} card={c} />
                              ))}
                              {!msg.isStreaming && optionBlocks.map((block, oi) => (
                                <ZenoOptions
                                  key={`${index}-options-${block.order ?? oi}`}
                                  question={block.data?.question}
                                  options={block.data?.options}
                                  onSelect={handleSelectOption}
                                  disabled={loading}
                                />
                              ))}
                              {!msg.isStreaming && (
                                <ZenoToolTrace
                                  tools={msg.metadata?.toolsUsed || msg.metadata?.tools_used}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {isTyping && !messages.some(m => m.isStreaming) && (() => {
                      const lastMsg = messages[messages.length - 1];
                      const lastAssistantHasContent = lastMsg?.role === 'assistant' &&
                        (lastMsg.message || lastMsg.content || lastMsg.response);
                      return !lastAssistantHasContent;
                    })() && (
                      <div className="flex items-start gap-3">
                        <img src={zenoAvatar} alt="Zeno" className="mt-1 h-8 w-8 flex-shrink-0 rounded-full object-cover" />
                        <div className="flex items-center gap-3 rounded-2xl border border-white/[0.12] bg-[#212129] px-5 py-4 shadow-[0_2px_10px_rgba(0,0,0,0.25)]">
                          <ZenitThinking size={22} />
                          <span className="text-gray-400">Thinking…</span>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                {/* Composer */}
                <div className="border-t border-white/10 p-4">
                  <div className="mx-auto w-full max-w-3xl">
                    {renderComposer()}
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============ RIGHT — Zeno panel. Desktop: inline column.
              Mobile: overlay drawer from the right. ============ */}
          {isMobile && showZenoPanel && (
            <div
              className="fixed inset-y-0 right-0 z-[65] bg-black/50 lg:hidden rail-left"
              onClick={() => setShowZenoPanel(false)}
            />
          )}
          {showZenoPanel && (
            <aside
              className={`flex h-full flex-col overflow-hidden border border-white/10 bg-[#141416] ${
                isMobile
                  ? 'fixed inset-y-0 right-0 z-[70] w-[300px] max-w-[85vw]'
                  : 'w-[300px] shrink-0 rounded-[20px]'
              }`}
            >
              <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <img src={zenoAvatar} alt="Zeno" className="h-7 w-7 flex-shrink-0 rounded-full object-cover" />
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-sm font-semibold text-white">Zeno</div>
                    <div className="truncate text-[11px] text-gray-400">Your workspace</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowZenoPanel(false)}
                  title="Collapse"
                  className="flex-shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3 space-y-3">
                {/* What you can ask Zeno — discovery first, so users see the
                    capability set before anything else. */}
                <ZenoCapabilities onPick={applyPrompt} />

                {/* Quick create card */}
                <div className="rounded-2xl border border-white/10 bg-[#1b1b1f] p-4">
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Sparkles className="h-3.5 w-3.5 text-[#b98bff]" />
                    Quick create
                  </div>
                  <div className="space-y-2">
                    <button
                      ref={avatarBtnRef}
                      onClick={() => setShowQuickAvatar((v) => !v)}
                      className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium text-white transition-all ${
                        showQuickAvatar
                          ? 'border-[#8844F5] bg-[#8844F5]/25'
                          : 'border-[#8844F5]/40 bg-[#8844F5]/15 hover:bg-[#8844F5]/25'
                      }`}
                    >
                      <Plus className="h-4 w-4 text-[#b98bff]" />
                      New avatar
                    </button>
                    <button
                      ref={agentBtnRef}
                      onClick={() => setShowQuickAgent((v) => !v)}
                      className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm text-gray-200 transition-all ${
                        showQuickAgent
                          ? 'border-[#8844F5]/60 bg-[#232327]'
                          : 'border-white/10 bg-[#232327] hover:border-[#8844F5]/50'
                      }`}
                    >
                      <Plus className="h-4 w-4 text-[#b98bff]" />
                      New agent
                    </button>

                    {/* More actions — opens a popover with the (soon) actions */}
                    <button
                      ref={moreActionsBtnRef}
                      onClick={() => setShowMoreActions((v) => !v)}
                      className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${
                        showMoreActions
                          ? "border-white/20 bg-[#232327] text-gray-200"
                          : "border-white/10 bg-[#232327] text-gray-400 hover:text-gray-200"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        More actions
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${showMoreActions ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Recent — one compact tabbed card (avatars · agents · workflows) */}
                <RecentPanel
                  tab={recentTab}
                  onTab={setRecentTab}
                  refreshing={refreshingRecent}
                  onRefresh={handleRefreshRecent}
                  avatars={recentAvatars}
                  agents={recentAgents}
                  workflows={recentWorkflows}
                  onOpenView={(v) => navigate(`/dashboard?view=${v}`)}
                />
              </div>
            </aside>
          )}
        </div>
      </main>

      {/* Quick create → Avatar: popover that unfolds from the "New avatar"
          button toward the chat. No dimming; the right menu stays visible. */}
      {showQuickAvatar && avatarPopover && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setShowQuickAvatar(false)}
          />
          <div
            className="fixed z-[70] w-[440px] max-w-[calc(100vw-2rem)]"
            style={{ top: avatarPopover.top, right: avatarPopover.right }}
          >
            <div
              className="overflow-y-auto rounded-xl shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
              style={{ maxHeight: avatarPopover.maxHeight }}
            >
              <QuickCreateAvatar
                open
                onClose={() => setShowQuickAvatar(false)}
                accountId={currentAccount?.id || user?.account_id || user?.accountId}
                userId={user?.user_id || user?.id || user?.sub}
                projectId={projects?.[0]?.id}
                onSendToChat={(text) => {
                  setShowQuickAvatar(false);
                  startNewConversation();
                  setMessage(text);
                  setTimeout(() => textareaRef.current?.focus(), 0);
                }}
                onCreated={() => loadZenoChats()}
              />
            </div>
            {/* Tail pointing right, toward the button */}
            <div
              className="absolute right-[-6px] h-3.5 w-3.5 rotate-45 border-r border-t border-[#8844F5]/30 bg-[#141018]"
              style={{ top: avatarPopover.tailTop }}
            />
          </div>
        </>
      )}

      {/* Quick create → Agent: popover, same mechanism as the avatar one. */}
      {showQuickAgent && agentPopover && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setShowQuickAgent(false)} />
          <div
            className="fixed z-[70] w-[440px] max-w-[calc(100vw-2rem)]"
            style={{ top: agentPopover.top, right: agentPopover.right }}
          >
            <div
              className="overflow-y-auto rounded-xl shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
              style={{ maxHeight: agentPopover.maxHeight }}
            >
              <QuickCreateAgent
                open
                onClose={() => setShowQuickAgent(false)}
                accountId={currentAccount?.id || user?.account_id || user?.accountId}
                onSendToChat={(text) => {
                  setShowQuickAgent(false);
                  startNewConversation();
                  setMessage(text);
                  setTimeout(() => textareaRef.current?.focus(), 0);
                }}
              />
            </div>
            <div
              className="absolute right-[-6px] h-3.5 w-3.5 rotate-45 border-r border-t border-[#8844F5]/30 bg-[#141018]"
              style={{ top: agentPopover.tailTop }}
            />
          </div>
        </>
      )}

      {/* More actions popover — floats over the chat, right menu stays intact. */}
      {showMoreActions && moreActionsPopover && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setShowMoreActions(false)}
          />
          <div
            className="fixed z-[70] w-[248px] max-w-[calc(100vw-2rem)]"
            style={{ top: moreActionsPopover.top, right: moreActionsPopover.right }}
          >
            <div className="overflow-hidden rounded-xl border border-white/10 bg-[#1b1b1f] p-2 shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
              <div className="px-1 pb-1.5 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                More actions
              </div>
              <div className="space-y-1.5">
                <button
                  disabled
                  title="Coming soon"
                  className="flex w-full cursor-default items-center gap-2 rounded-xl border border-white/10 bg-[#232327] px-3 py-2.5 text-left text-sm text-gray-500"
                >
                  <WorkflowIcon className="h-4 w-4" />
                  New workflow
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-600">soon</span>
                </button>
                <button
                  disabled
                  title="Coming soon"
                  className="flex w-full cursor-default items-center gap-2 rounded-xl border border-white/10 bg-[#232327] px-3 py-2.5 text-left text-sm text-gray-500"
                >
                  <WorkflowIcon className="h-4 w-4" />
                  Add workflow to an agent
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-600">soon</span>
                </button>
              </div>
            </div>
            {/* Tail pointing right, toward the button */}
            <div
              className="absolute right-[-6px] h-3.5 w-3.5 rotate-45 border-r border-t border-white/10 bg-[#1b1b1f]"
              style={{ top: moreActionsPopover.tailTop }}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Compact right-panel card: recent avatars/agents/workflows in one tabbed card
// (saves vertical space). "View more" opens Projects in a new browser tab.
function RecentPanel({ tab, onTab, refreshing, onRefresh, avatars, agents, workflows, onOpenView }) {
  const TABS = [
    { key: "avatars", label: "Avatars", icon: UserSquare, items: avatars, empty: "No avatars yet", view: "Avatars" },
    { key: "agents", label: "Agents", icon: Headset, items: agents, empty: "No agents yet", view: "Assistants" },
    { key: "workflows", label: "Workflows", icon: WorkflowIcon, items: workflows, empty: "No workflows yet", view: "Workflows" },
  ];
  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const ActiveIcon = active.icon;
  const openViewMore = () =>
    window.open("/dashboard?view=Projects", "_blank", "noopener,noreferrer");
  return (
    <div className="rounded-2xl border border-white/10 bg-[#1b1b1f] p-3">
      {/* Tabs + refresh */}
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex gap-0.5 rounded-lg bg-[#141416] p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => onTab(t.key)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                tab === t.key ? "bg-[#2a2a30] text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          title="Refresh"
          className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
        >
          <RotateCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {active.items.length === 0 ? (
        <p className="px-1 py-2 text-xs text-gray-500">{active.empty}</p>
      ) : (
        <div className="space-y-1.5">
          {active.items.map((it) => (
            <button
              key={it.id}
              onClick={() => onOpenView(active.view)}
              className="flex w-full items-center gap-2.5 rounded-xl border border-white/10 bg-[#232327] px-3 py-2 text-left transition-all hover:border-[#8844F5]/50"
            >
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#14121a]">
                {it.avatar ? (
                  <img src={it.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ActiveIcon className="h-3.5 w-3.5 text-[#b98bff]" />
                )}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-gray-200">
                {it.name || "Untitled"}
              </span>
            </button>
          ))}
        </div>
      )}

      <button
        onClick={openViewMore}
        title="Open in a new tab"
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 px-3 py-2 text-xs text-gray-400 transition-all hover:border-[#8844F5]/40 hover:text-white"
      >
        View more <ExternalLink className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
